import React from 'react';
import type { QuestGuidanceLocation } from '../../../../../shared/content/generated/quests.gen';
import { MAP_PREVIEWS, mapPreviewUrl } from './mapPreviews.gen';
import { frameGuidanceWindow, GUIDANCE_CARD_ASPECT, windowContains, type WorldPoint } from './guidanceFraming';

interface GuidanceMapCardProps {
  /** 'turn-in' marks one NPC; 'objective' marks an area. */
  variant: 'turn-in' | 'objective';
  location: QuestGuidanceLocation;
  /** Caption kicker, e.g. "Report to" / "Hunt here". */
  kicker: string;
  /** Caption subject, e.g. "Warden Lark". */
  title: string;
  /** Art beside the caption (mob preview, item icon, NPC glyph). */
  art?: React.ReactNode;
  /** The player's world position when they stand on this map. */
  you?: WorldPoint | null;
  testId?: string;
  /** Map window aspect (width / height); wider when the card has the row to itself. */
  aspect?: number;
}

/**
 * A framed window onto the generated map preview (public/map-previews/), with
 * a pulsing marker on the target, an area halo for objectives and a "you" dot
 * when the player is on the same map. Everything inside the SVG is in world
 * pixels, so markers line up with the preview by construction.
 */
export const GuidanceMapCard: React.FC<GuidanceMapCardProps> = ({
  variant, location, kicker, title, art, you, testId, aspect = GUIDANCE_CARD_ASPECT,
}) => {
  const preview = MAP_PREVIEWS[location.mapId];
  const mapName = preview?.name ?? location.mapId;
  const caption = <figcaption className="npc-guidance-caption">
    {art && <span className="npc-guidance-art">{art}</span>}
    <span className="npc-guidance-text">
      <span className="npc-card-kicker">{kicker}</span>
      <strong>{title}</strong>
      <span className="npc-guidance-map-name">{mapName}</span>
    </span>
  </figcaption>;

  if (!preview) {
    // No generated preview (should not happen: map-previews-check gates it). Keep the words.
    return <figure className="npc-guidance-card" data-guidance={variant} data-testid={testId}>{caption}</figure>;
  }

  const win = frameGuidanceWindow(location.points, preview.width, preview.height, aspect);
  const unit = win.width / 100; // marker sizes scale with the window, not the world
  const viewBox = `${win.x} ${win.y} ${win.width} ${win.height}`;
  const points = location.points;
  // One pulse per card: on the pin, or on the area's centre. An HTML ring over the map rather than
  // an SVG circle, so its transform/opacity animation runs on the compositor and nothing repaints
  // per frame (an animated SVG child repaints its whole page every frame).
  const pulse = points.length === 0 ? null : {
    x: points.reduce((a, p) => a + p.x, 0) / points.length,
    y: points.reduce((a, p) => a + p.y, 0) / points.length,
    r: unit * (points.length > 1 ? 5 : 3),
  };
  const youInside = you ? windowContains(win, you) : false;
  const youShown = you ? {
    x: Math.min(Math.max(you.x, win.x + unit * 2), win.x + win.width - unit * 2),
    y: Math.min(Math.max(you.y, win.y + unit * 2), win.y + win.height - unit * 2),
  } : null;

  return <figure className="npc-guidance-card" data-guidance={variant} data-testid={testId}
    data-map-id={location.mapId}>
    <div className="npc-guidance-map" style={{ aspectRatio: `${aspect}` }}>
      <svg viewBox={viewBox} preserveAspectRatio="xMidYMid meet" role="img"
        aria-label={`${kicker} ${title}, ${mapName}`} data-view-window={viewBox}>
        <image href={mapPreviewUrl(location.mapId)} x={0} y={0} width={preview.width} height={preview.height} />
        {variant === 'objective' && points.length > 1 && <g className="npc-guidance-area">
          {points.map((p, i) => <circle key={i} cx={p.x} cy={p.y} r={unit * 2.4} />)}
        </g>}
        {points.map((p, i) => <g key={i} className="npc-guidance-marker" transform={`translate(${p.x} ${p.y})`}>
          {variant === 'turn-in'
            ? <path className="npc-guidance-pin" d={`M0 ${-unit * 0.4} l${-unit * 1.6} ${-unit * 3} a${unit * 1.7} ${unit * 1.7} 0 1 1 ${unit * 3.2} 0 z`} />
            : <circle className="npc-guidance-dot" r={unit * 0.8} />}
        </g>)}
        {youShown && <g className="npc-guidance-you" data-you-offscreen={!youInside || undefined}
          transform={`translate(${youShown.x} ${youShown.y})`}>
          <circle r={unit * 1.2} />
        </g>}
      </svg>
      {pulse && <span className="npc-guidance-pulse" aria-hidden="true" data-area-pulse={points.length > 1 || undefined}
        style={{ left: `${(pulse.x - win.x) / win.width * 100}%`, top: `${(pulse.y - win.y) / win.height * 100}%`,
          width: `${pulse.r * 2 / win.width * 100}%` }} />}
    </div>
    {caption}
  </figure>;
};
